import { useState, useEffect } from "react";
import {
  Typography, Button, Stack, Box, Card, CardContent,
  IconButton, Chip, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryIcon from "@mui/icons-material/Category";
import {
  collection, onSnapshot, doc, setDoc, deleteDoc, serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase/config";

interface DistripakoCategory {
  id?: string;
  name: string;
  tagline: string;
  description: string;
  iconName: string;
  image: string;
  itemCount: number;
  active: boolean;
  order: number;
}

const empty: DistripakoCategory = {
  name: "", tagline: "", description: "", iconName: "HardHat",
  image: "", itemCount: 0, active: true, order: 0,
};

const ICON_OPTIONS = ["HardHat", "Briefcase", "Stethoscope", "UtensilsCrossed", "Sparkles", "Shield", "Package", "Star"];

export default function DistripakoCategoriasPage() {
  const [items, setItems] = useState<DistripakoCategory[]>([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<DistripakoCategory>(empty);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "categorias"), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() } as DistripakoCategory));
      data.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setItems(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleOpen = (item?: DistripakoCategory) => {
    setCurrent(item ? { ...item } : { ...empty, order: items.length });
    setOpen(true);
  };

  const handleSave = async () => {
    const id = current.id || doc(collection(db, "categorias")).id;
    await setDoc(doc(db, "categorias", id), {
      ...current, id,
      updatedAt: serverTimestamp(),
      ...(current.id ? {} : { createdAt: serverTimestamp() }),
    }, { merge: true });
    setOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("¿Eliminar esta categoría del sitio web?")) {
      await deleteDoc(doc(db, "categorias", id));
    }
  };

  const uploadImage = async (file: File) => {
    setUploading(true);
    try {
      const storageRef = ref(storage, `categorias/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setCurrent((prev) => ({ ...prev, image: url }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={800}>🗂️ Categorías del Sitio Web</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestiona las categorías de uniformes que se muestran en distripako.com
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
          sx={{ borderRadius: 2, fontWeight: 700, px: 3 }}>
          Nueva Categoría
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))" gap={2.5}>
          {items.map((item) => (
            <Card key={item.id} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              {item.image && (
                <Box component="img" src={item.image} alt={item.name}
                  sx={{ width: "100%", height: 120, objectFit: "cover" }} />
              )}
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                      <Typography fontWeight={800} fontSize="1rem">{item.name}</Typography>
                      <Chip label={item.active ? "Activa" : "Inactiva"} size="small"
                        color={item.active ? "success" : "default"} />
                    </Stack>
                    <Typography variant="caption" color="primary" fontWeight={700}>{item.tagline}</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5} sx={{ fontSize: "0.8rem" }}>
                      {item.description}
                    </Typography>
                    <Stack direction="row" gap={1} mt={1}>
                      <Chip label={`Icono: ${item.iconName}`} size="small" variant="outlined" />
                      <Chip label={`Orden: ${item.order}`} size="small" variant="outlined" />
                    </Stack>
                  </Box>
                  <Stack>
                    <IconButton size="small" onClick={() => handleOpen(item)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item.id!)}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && (
            <Box gridColumn="1/-1" textAlign="center" py={8} color="text.secondary">
              <CategoryIcon sx={{ fontSize: 64, opacity: 0.2, mb: 2, display: "block", mx: "auto" }} />
              <Typography>No hay categorías. Crea la primera.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {current.id ? "✏️ Editar Categoría Web" : "📂 Nueva Categoría Web"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={1}>
            <TextField label="Nombre" value={current.name} fullWidth
              onChange={(e) => setCurrent((p) => ({ ...p, name: e.target.value }))} />
            <TextField label="Tagline (ej: Ropa Industrial)" value={current.tagline} fullWidth
              onChange={(e) => setCurrent((p) => ({ ...p, tagline: e.target.value }))} />
            <TextField label="Descripción" value={current.description} fullWidth multiline rows={2}
              onChange={(e) => setCurrent((p) => ({ ...p, description: e.target.value }))} />
            <TextField label="Nombre del Icono (ej: HardHat)" value={current.iconName} fullWidth
              helperText={`Opciones: ${ICON_OPTIONS.join(", ")}`}
              onChange={(e) => setCurrent((p) => ({ ...p, iconName: e.target.value }))} />
            <TextField label="Orden (número)" type="number" value={current.order} fullWidth
              onChange={(e) => setCurrent((p) => ({ ...p, order: Number(e.target.value) }))} />

            {/* Image */}
            <Stack direction="row" spacing={2} alignItems="center">
              {current.image && (
                <Box component="img" src={current.image} sx={{ width: 100, height: 65, objectFit: "cover", borderRadius: 2 }} />
              )}
              <Button component="label" variant="outlined" startIcon={uploading ? <CircularProgress size={16} /> : undefined}
                disabled={uploading} sx={{ borderRadius: 2 }}>
                {uploading ? "Subiendo..." : "Subir Imagen"}
                <input hidden type="file" accept="image/*"
                  onChange={(e) => e.target.files && uploadImage(e.target.files[0])} />
              </Button>
            </Stack>

            <FormControlLabel
              control={<Switch checked={current.active} onChange={(e) => setCurrent((p) => ({ ...p, active: e.target.checked }))} />}
              label="Visible en el sitio web"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setOpen(false)} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={handleSave} sx={{ borderRadius: 2, px: 4, fontWeight: 800 }}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
